<template>
  <b-modal
    id="modal-reject-leave"
    ref="inner-modal"
    title="Reject Leave Request"
    ok-title="Reject"
    cancel-title="Cancel"
    ok-variant="danger"
    cancel-variant="outline-secondary"
    size="lg"
    modal-class="leave-action-modal leave-reject-modal"
    body-class="leave-action-modal__body"
    @ok.prevent="handleOk"
    @hidden="handleHidden"
  >
    <b-form v-if="leave" class="leave-action-form">
      <b-card no-body class="border leave-action-panel mb-2">
        <b-card-body class="p-2">
          <div class="d-flex align-items-center flex-wrap mb-1 leave-action-panel__header">
            <h6 class="mb-0 leave-action-panel__title mr-2">
              <feather-icon icon="CalendarIcon" size="14" class="mr-50 text-primary" />
              {{ formatLeaveTypeLabel(leave.leave_type) }}
            </h6>
            <b-badge :variant="statusVariant" class="mb-0">{{ statusLabel }}</b-badge>
          </div>

          <b-row class="leave-action-columns">
            <b-col cols="12" md="6" class="mb-1 mb-md-0">
              <div class="leave-detail-grid">
                <div class="leave-detail-item">
                  <div class="leave-detail-label">Employee</div>
                  <div class="leave-detail-value">{{ employeeName || '-' }}</div>
                </div>
                <div class="leave-detail-item">
                  <div class="leave-detail-label">Duration</div>
                  <div class="leave-detail-value">{{ durationLabel }}</div>
                </div>
                <div class="leave-detail-item">
                  <div class="leave-detail-label">From</div>
                  <div class="leave-detail-value">{{ formatDate(leave.from_date) || '-' }}</div>
                </div>
                <div class="leave-detail-item">
                  <div class="leave-detail-label">To</div>
                  <div class="leave-detail-value">{{ formatDate(leave.to_date) || '-' }}</div>
                </div>
              </div>
            </b-col>
            <b-col cols="12" md="6">
              <div class="mb-1">
                <div class="leave-detail-label">Comments</div>
                <p class="mb-0 leave-detail-note">{{ leave.comment || 'No comments' }}</p>
              </div>
              <div class="mb-0">
                <div class="leave-detail-label">Remarks by Manager</div>
                <p class="mb-0 leave-detail-note">{{ leave.manager_remarks || leave.manager_comment || 'No remarks from manager' }}</p>
              </div>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <b-form-group
        label="Rejection Comment"
        label-for="rejection-comment"
        class="mb-0 leave-action-comment"
        label-class="small font-weight-bolder mb-50"
      >
        <b-form-textarea
          id="rejection-comment"
          v-model="localComment"
          rows="2"
          max-rows="3"
          placeholder="Add a reason for rejection"
          :state="commentState"
          class="leave-action-textarea"
          @blur="commentTouched = true"
          @input="onCommentInput"
        />
        <b-form-invalid-feedback :state="commentState">
          Comment is mandatory for rejection
        </b-form-invalid-feedback>
      </b-form-group>
    </b-form>
  </b-modal>
</template>

<script>
import moment from 'moment'
import {
  BModal, BForm, BFormGroup, BFormTextarea, BFormInvalidFeedback, BBadge, BCard, BCardBody, BRow, BCol,
} from 'bootstrap-vue'

export default {
  name: 'RejectLeaveModal',
  components: {
    BModal, BForm, BFormGroup, BFormTextarea, BFormInvalidFeedback, BBadge, BCard, BCardBody, BRow, BCol,
  },
  props: {
    leave: { type: Object, default: null },
    employeeName: { type: String, default: '' },
    formatLeaveType: { type: Function, default: null },
    formatApprovalStatus: { type: Function, default: null },
    getApprovalStatusVariant: { type: Function, default: null },
  },
  data() {
    return {
      localComment: '',
      commentTouched: false,
      commentSubmitAttempted: false,
    }
  },
  computed: {
    hasComment() {
      return !!(this.localComment && this.localComment.trim())
    },
    commentState() {
      if (!(this.commentTouched || this.commentSubmitAttempted)) return null
      return this.hasComment
    },
    durationLabel() {
      if (!this.leave || this.leave.total_days == null) return '-'
      const days = parseFloat(this.leave.total_days)
      if (Number.isNaN(days)) return '-'
      return days === 1 ? '1 day' : `${days} days`
    },
    statusLabel() {
      const status = this.leave && this.leave.status
      if (typeof this.formatApprovalStatus === 'function') {
        return this.formatApprovalStatus(status) || status || 'Pending'
      }
      return status || 'Pending'
    },
    statusVariant() {
      const status = this.leave && this.leave.status
      if (typeof this.getApprovalStatusVariant === 'function') {
        return this.getApprovalStatusVariant(status) || 'secondary'
      }
      return 'secondary'
    },
  },
  methods: {
    formatLeaveTypeLabel(type) {
      if (typeof this.formatLeaveType === 'function') return this.formatLeaveType(type) || type || '-'
      return type || '-'
    },
    formatDate(date) {
      if (!date) return ''
      return moment(date).format('DD MMM YYYY')
    },
    onCommentInput() {
      if (this.hasComment) {
        this.commentSubmitAttempted = false
      }
    },
    handleOk() {
      this.commentSubmitAttempted = true
      if (!this.hasComment) return
      this.$emit('confirm', { comment: this.localComment.trim() })
    },
    handleHidden() {
      this.localComment = ''
      this.commentTouched = false
      this.commentSubmitAttempted = false
      this.$emit('hidden')
    },
    show() { this.$refs['inner-modal'].show() },
    hide() { this.$refs['inner-modal'].hide() },
  },
}
</script>

<style lang="scss">
.leave-action-modal.leave-reject-modal {
  .modal-dialog {
    max-width: 720px;
  }

  .modal-footer .btn-danger {
    font-weight: 600;
  }
}

/* Shared compact styles (also defined on ApproveLeaveModal; kept here so Reject works standalone) */
.leave-action-modal {
  .leave-action-modal__body {
    padding: 0.75rem 1rem;
  }

  .leave-action-columns {
    align-items: flex-start;
  }

  .leave-action-panel {
    background: #f8f9fb;
    border-color: #e5e7eb !important;
    height: auto;

    .card-body {
      padding: 0.65rem 0.75rem !important;
    }
  }

  .leave-action-panel__header {
    min-height: auto;
  }

  .leave-action-panel__title {
    font-size: 0.8125rem;
    font-weight: 700;
    color: #1f2937;
    line-height: 1.25;
  }

  .leave-detail-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.4rem 0.75rem;
  }

  .leave-detail-label {
    font-size: 0.68rem;
    font-weight: 700;
    line-height: 1.2;
    margin-bottom: 0.05rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #6b7280;
  }

  .leave-detail-value {
    font-size: 0.8125rem;
    font-weight: 600;
    line-height: 1.3;
    color: #111827;
  }

  .leave-detail-note {
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1.35;
    color: #4b5563;
    max-height: 2.7em;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .leave-action-comment {
    margin-bottom: 0.35rem !important;
  }

  .leave-action-textarea {
    font-size: 0.8125rem;
    min-height: 52px;
  }
}
</style>
